<script setup lang="ts">
import DonateFiscalBanner from "./DonateFiscalBanner.vue";

withDefaults(defineProps<{
  includeFees: boolean;
  feesAmount: number;
  finalAmount: number;
  feesText: string;
  formatAmount: (amount: number) => string;
  donateUs?: boolean;
  donateToUsAmount?: number;
  isPreviewMode?: boolean;
  showFeesOption?: boolean;
  /**
   * Annonce « 66 % déductible + reçu fiscal ». Fermée par défaut : un appelant
   * qui oublie de la passer ne doit jamais promettre un reçu fiscal (cf.
   * utils/recuFiscal.ts, Karim 2026-10-04).
   */
  showFiscalInfo?: boolean;
  organizationName?: string;
  /** Provider primaire — affecte le libellé « Couvrir les frais X ». */
  providerLabel?: "stripe" | "paypal";
  /** Don récurrent : « chaque don de X ne vous coûte que Y ». */
  isSubscription?: boolean;
  /** Ordinateur : encart fiscal affiché dans la colonne de gauche de la page. */
  fiscalInSidebarOnDesktop?: boolean;
  /** Forme des boutons de la page (`pill` : compteur « Soutenir Donoor » en pilule). */
  buttonStyle?: string | null;
}>(), {
  showFeesOption: true,
  showFiscalInfo: false,
  donateUs: false,
  donateToUsAmount: 0,
  isPreviewMode: false,
  organizationName: "",
  providerLabel: "stripe",
  isSubscription: false,
  fiscalInSidebarOnDesktop: false,
  buttonStyle: null,
});

const emit = defineEmits<{
  "update:includeFees": [value: boolean];
  "update:donateToUsAmount": [value: number];
}>();
</script>

<template>
  <div class="space-y-3">
    <!-- Réduction d'impôt. Sur ordinateur, les pages à deux colonnes
         l'affichent dans la colonne d'informations à gauche. -->
    <DonateFiscalBanner
      v-if="showFiscalInfo"
      :class="{ 'lg:hidden': fiscalInSidebarOnDesktop }"
      :final-amount="finalAmount"
      :format-amount="formatAmount"
      :is-subscription="isSubscription"
    />

    <!-- Options (Couvrir les frais + Soutenir Donoor) -->
    <div class="rounded-xl bg-gray-100 dark:bg-gray-800">
      <!-- Couvrir les frais -->
      <div v-if="showFeesOption !== false" class="px-3 py-3 lg:p-4">
        <DonateFeesOption
          :include-fees="includeFees"
          :fees-amount="feesAmount"
          :final-amount="finalAmount"
          :fees-text="feesText"
          :format-amount="formatAmount"
          :provider-label="providerLabel"
          @update:include-fees="emit('update:includeFees', $event)"
        />
      </div>

      <!-- Séparateur + Soutenir Donoor (caché en mode preview) -->
      <template v-if="donateUs && !isPreviewMode">
        <div v-if="showFeesOption !== false" class="border-t border-gray-200 dark:border-gray-700" />
        <div class="px-3 py-3 lg:p-4">
          <DonateSupportDonoor
            :donate-to-us-amount="donateToUsAmount ?? 0"
            :format-amount="formatAmount"
            :organization-name="organizationName"
            :pilule="buttonStyle === 'pill'"
            @update:donate-to-us-amount="
              emit('update:donateToUsAmount', $event)
            "
          />
        </div>
      </template>
    </div>
  </div>
</template>
